import { Platform, Linking, Alert } from 'react-native';
import { getApiUrl } from './apiConfig';

const OAUTH_MSG_TYPE = 'kungfu_youtube_oauth';
export const YOUTUBE_OAUTH_RETURN_STORAGE_KEY = 'kungfu_youtube_oauth_return_href';
const RETURN_STORAGE_KEY = YOUTUBE_OAUTH_RETURN_STORAGE_KEY;
const OAUTH_IN_PROGRESS_KEY = 'kungfu_youtube_oauth_in_progress';
const PENDING_RESULT_KEY = 'kungfu_youtube_oauth_pending_result';
const POPUP_NAME = 'kungfu_youtube_oauth';

/** @typedef {{ success: boolean, error?: string, closed?: boolean, checkServer?: boolean }} YoutubeOAuthResult */

/** @type {Set<(result: YoutubeOAuthResult) => void>} */
const completeListeners = new Set();

/**
 * Subscribe to OAuth completion (popup message or full-page return).
 * @param {(result: YoutubeOAuthResult) => void} listener
 * @returns {() => void}
 */
export function subscribeYoutubeOAuthComplete(listener) {
  completeListeners.add(listener);
  return () => completeListeners.delete(listener);
}

function notifyComplete(result) {
  completeListeners.forEach((fn) => {
    try {
      fn(result);
    } catch (e) {
      console.warn('youtube OAuth listener:', e);
    }
  });
}

function buildReturnToUrl() {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return window.location.href.split('#')[0];
  }
  return '';
}

function markOAuthInProgress() {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(OAUTH_IN_PROGRESS_KEY, '1');
  const href = buildReturnToUrl();
  if (href) sessionStorage.setItem(RETURN_STORAGE_KEY, href);
}

function clearOAuthInProgress() {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
  sessionStorage.removeItem(OAUTH_IN_PROGRESS_KEY);
  sessionStorage.removeItem(RETURN_STORAGE_KEY);
  sessionStorage.removeItem(PENDING_RESULT_KEY);
}

export function isYoutubeOAuthInProgress() {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(OAUTH_IN_PROGRESS_KEY) === '1';
}

/**
 * Handle ?youtube_connected= on the main window without a full page reload.
 * @returns {boolean} true when OAuth return params were handled
 */
export function restoreYoutubeOAuthReturnHrefIfPresent() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
  const u = new URL(window.location.href);
  const connected = u.searchParams.get('youtube_connected');
  const err = u.searchParams.get('youtube_error');
  if (connected !== '1' && !err) return false;

  const result =
    connected === '1'
      ? { success: true }
      : { success: false, error: err || 'YouTube connection failed' };

  let stored = null;
  if (typeof sessionStorage !== 'undefined') {
    stored = sessionStorage.getItem(RETURN_STORAGE_KEY);
    sessionStorage.setItem(PENDING_RESULT_KEY, JSON.stringify(result));
    sessionStorage.removeItem(OAUTH_IN_PROGRESS_KEY);
    sessionStorage.removeItem(RETURN_STORAGE_KEY);
  }

  try {
    if (stored) {
      const target = new URL(stored);
      const clean = `${target.pathname}${target.search}${target.hash || ''}`;
      window.history.replaceState({}, '', clean);
    } else {
      u.searchParams.delete('youtube_connected');
      u.searchParams.delete('youtube_error');
      const qs = u.searchParams.toString();
      window.history.replaceState(
        {},
        '',
        `${u.pathname}${qs ? `?${qs}` : ''}${u.hash || ''}`
      );
    }
  } catch {
    /* keep current URL */
  }

  return true;
}

export function consumeYoutubeOAuthQueryParams() {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;

  if (typeof sessionStorage !== 'undefined') {
    const raw = sessionStorage.getItem(PENDING_RESULT_KEY);
    if (raw) {
      sessionStorage.removeItem(PENDING_RESULT_KEY);
      clearOAuthInProgress();
      try {
        return JSON.parse(raw);
      } catch {
        return { success: true };
      }
    }
  }

  const u = new URL(window.location.href);
  const connected = u.searchParams.get('youtube_connected');
  const err = u.searchParams.get('youtube_error');
  if (connected !== '1' && !err) return null;

  if (connected === '1') u.searchParams.delete('youtube_connected');
  if (err) u.searchParams.delete('youtube_error');
  const qs = u.searchParams.toString();
  window.history.replaceState({}, '', `${u.pathname}${qs ? `?${qs}` : ''}${u.hash || ''}`);
  clearOAuthInProgress();

  if (err) {
    let message = err;
    try {
      message = decodeURIComponent(err);
    } catch {
      /* keep raw */
    }
    return { success: false, error: message };
  }
  return { success: true };
}

let returnListenerInstalled = false;

/** Call once (e.g. from YoutubeOAuthBar) to handle OAuth return on load. */
export function installYoutubeOAuthReturnListener() {
  if (returnListenerInstalled) return;
  returnListenerInstalled = true;

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const result = consumeYoutubeOAuthQueryParams();
    if (result) notifyComplete(result);
  }
}

function buildOAuthStartUrl({ accountKey, forceConsent }) {
  const returnTo = buildReturnToUrl();

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const q = new URLSearchParams({ account_key: accountKey });
    if (returnTo) q.set('return_to', returnTo);
    q.set('force_consent', '1');
    const qs = q.toString();
    return `${window.location.origin}/youtube-oauth${qs ? `?${qs}` : ''}`;
  }

  const api = getApiUrl();

  const params = new URLSearchParams({
    account_key: accountKey,
    popup: '0',
  });
  if (returnTo) params.set('return_to', returnTo);
  params.set('force_consent', '1');
  return `${api}/api/youtube/oauth/start?${params.toString()}`;
}

/**
 * Open Google OAuth in a popup on web (same-origin bridge → API → Google).
 * @param {{ accountKey?: string, forceConsent?: boolean }} [options]
 * @returns {Promise<YoutubeOAuthResult>}
 */
export function openYoutubeOAuthConnect({ accountKey = 'default', forceConsent = true } = {}) {
  markOAuthInProgress();

  const url = buildOAuthStartUrl({ accountKey, forceConsent });

  if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.open === 'function') {
    return openYoutubeOAuthPopup(url);
  }

  return Linking.openURL(url).then(() => ({ success: true }));
}

/**
 * @param {string} oauthStartUrl
 * @returns {Promise<YoutubeOAuthResult>}
 */
function openYoutubeOAuthPopup(oauthStartUrl) {
  return new Promise((resolve) => {
    const apiOrigin = (() => {
      try {
        return new URL(getApiUrl()).origin;
      } catch {
        return '';
      }
    })();
    const appOrigin = window.location.origin;

    const popup = window.open(
      oauthStartUrl,
      POPUP_NAME,
      'width=520,height=720,menubar=no,toolbar=no,location=yes,status=no'
    );

    if (!popup) {
      clearOAuthInProgress();
      Alert.alert(
        'Pop-up blocked',
        'Allow pop-ups for this site, then tap Connect YouTube again.'
      );
      resolve({ success: false, error: 'popup_blocked' });
      return;
    }

    let settled = false;
    let closedSince = 0;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearInterval(pollTimer);
      window.removeEventListener('message', onMessage);
      clearOAuthInProgress();
      try {
        popup.close();
      } catch {
        /* ignore */
      }
      notifyComplete(result);
      resolve(result);
    };

    const onMessage = (event) => {
      const data = event.data;
      if (!data || data.type !== OAUTH_MSG_TYPE) return;
      if (apiOrigin && event.origin !== apiOrigin && event.origin !== appOrigin) {
        return;
      }
      if (data.ok) {
        finish({ success: true });
      } else {
        finish({ success: false, error: data.error || 'YouTube connection failed' });
      }
    };

    window.addEventListener('message', onMessage);

    const pollTimer = setInterval(() => {
      if (popup.closed) {
        if (!closedSince) closedSince = Date.now();
        if (Date.now() - closedSince > 1500 && !settled) {
          finish({ success: false, closed: true, checkServer: true });
        }
      } else {
        closedSince = 0;
      }
    }, 300);
  });
}

export { OAUTH_MSG_TYPE };
